/**
 * View types of the light questioning (UC-8, docs/slices/humint-questioning-light): a recorder
 * that saves the recording when it ends, a recording page with a player and the transcript by
 * voice, and the case's recordings as *Výsluchy* lists them. No session, bookmarks, speaker
 * roles or report yet — those are the prototypes beside these (`questioning-types.ts`).
 */
import type { ProcessingStage } from '@/components/processing/processing-stages'
import type { RecentEncounter } from '../encounter/encounter-types'
import type { SubjectPageProblem } from '../subject/SubjectPageNotice'
import type { CaseRole, ListReadFailure, ListReadProblem, SubjectSummary } from '../types'
import type { RecordingVoicesData } from '../voice/recording/recording-voice-types'
import type { RecordingStatus } from './questioning-types'

// ── Recorder ─────────────────────────────────────────────────────────────────

/**
 * A language the recorder can name for a recording, as a Whisper code. Several may be named, in
 * the order chosen, and none leaves one to detection (`recording-languages.ts`).
 */
export type RecordingLanguage = 'sk' | 'cs' | 'uk' | 'ru' | 'en' | 'de' | 'pl' | 'hu'

/** The languages in the order the chips show them. */
export const RECORDING_LANGUAGES: readonly RecordingLanguage[] = [
  'sk',
  'cs',
  'uk',
  'ru',
  'en',
  'de',
  'pl',
  'hu',
]

/**
 * Why this browser will not record: it has no recorder here (an old browser, or a page that is
 * not a secure context), the microphone was refused, or it could not be started.
 */
export type MicrophoneProblem = 'unsupported' | 'denied' | 'unavailable'

/** Why a picked file never became a recording. */
export type RecordingFileProblem = 'empty' | 'too_large' | 'not_audio' | 'unsupported_format'

/** What the recording screen shows of a recording in progress. */
export type LiveRecording = Pick<RecordingStatus, 'elapsedMs' | 'paused' | 'levels' | 'wakeLock'>

/** Why a recording is not saved yet. */
export type RecordingSaveProblem =
  | 'offline'
  | 'timeout'
  | 'audit_unavailable'
  | 'extension_off'
  | 'error'
  | 'forbidden'
  | 'too_large'
  | 'unsupported_format'
  | 'language'
  | 'subject_gone'
  | 'conflict'
  | 'rejected'

/** What may pass. Every other problem is the server's refusal of this very recording. */
const PASSING_SAVE_PROBLEMS: ReadonlySet<RecordingSaveProblem> = new Set([
  'offline',
  'timeout',
  'audit_unavailable',
  'extension_off',
  'error',
])

/** Whether sending the same recording again may work: a refusal of it gets the same answer. */
export const maySendAgain = (problem: RecordingSaveProblem): boolean =>
  PASSING_SAVE_PROBLEMS.has(problem)

/** Where a recording that has ended stands on its way to the server. */
export type RecordingSaveStatus =
  /** Held, not being sent: an interrupted recording is offered for saving. */
  | { kind: 'waiting' }
  /** Its hash is being computed. */
  | { kind: 'hashing' }
  /** `progress` 0..1 of the bytes sent. */
  | { kind: 'uploading'; progress: number }
  | { kind: 'failed'; problem: RecordingSaveProblem }

/** What the saving screen shows of the recording it holds. */
export interface HeldRecordingSummary {
  /** Recorded time; null for a picked file, whose length is not known before it is transcribed. */
  durationMs: number | null
  sizeBytes: number
  /** The microphone stopped on its own, or the recording reached the size limit. */
  interrupted: boolean
}

// ── Recording page ───────────────────────────────────────────────────────────

/** The state of the recording's job: transcription first, then the entities. */
export type RecordingJobState = 'processing' | 'done' | 'failed'

/** One stretch of speech by one voice. */
export interface RecordingTurn {
  index: number
  startMs: number
  endMs: number
  /** 1..n by first appearance; 0 = a voice the transcription could not tell. */
  voice: number
  text: string
  /**
   * The language the turn was transcribed in (a Whisper code, `sk`). Null or absent when the
   * transcript names none: the turn is then read in the recording's one language.
   */
  language?: string | null
}

export interface RecordingTranscript {
  /** `pending`: none stored yet; `empty`: stored, with no speech in it. */
  status: 'pending' | 'ready' | 'empty'
  voiceCount: number
  /** In time order. */
  turns: readonly RecordingTurn[]
}

/** The encounter a recording is held in, as every encounter row shows it (ADR-0057). */
export type RecordingEncounter = Pick<
  RecentEncounter,
  'ref' | 'type' | 'at' | 'location' | 'placeNote' | 'unit'
>

/** One of a subject's encounters as a recording may be held in it, with who else was in it. */
export interface RecordingEncounterOption {
  encounter: RecordingEncounter
  /** The other subjects of the encounter, without the subject whose encounters these are. */
  others: readonly SubjectSummary[]
}

/** A subject's encounters, newest first: being read, read, or why they could not be. */
export type RecordingEncountersState =
  | { status: 'loading' }
  | { status: 'ready'; options: readonly RecordingEncounterOption[] }
  | { status: 'failed'; problem: ListReadProblem }

/** Everything the recording page shows. */
export interface RecordingData {
  captureId: string
  /** Local cache revision: each accepted reprocess starts a new polling/read cycle. */
  reprocessId?: string
  /** The subject who holds the recording: its file is attached to them. */
  subject: SubjectSummary
  /** Null for none, or for an encounter the workspace cannot show. */
  encounter: RecordingEncounter | null
  /** The other subjects who took part, in the order they were linked. */
  participants: readonly SubjectSummary[]
  /** When the recording started, ISO. */
  capturedAt: string
  state: RecordingJobState
  /**
   * The stage the latest job is in: queued, transcript, indexing, entities, done. Null or absent
   * from a server that names no stage; the page then goes by `state` and the transcript alone.
   */
  stage?: ProcessingStage | null
  /** The stage the job failed in, else null. */
  failedStage?: ProcessingStage | null
  durationMs: number | null
  /**
   * One language of the recording, for what shows one: the one with the most speech; before the
   * transcription, the first one named. Null while none is known.
   */
  language: string | null
  /**
   * Every language the recording was transcribed in, in the order first spoken; before the
   * transcript, the ones named for it. Empty while none is known.
   */
  languages: readonly string[]
  /**
   * Why the recording is in one language although several were named for it (the transcription's
   * short code). Null or absent when it was transcribed as asked.
   */
  languageFallback?: string | null
  /** Auth-gated `/api/v1/files/…` path of the audio. */
  audioUrl: string
  /** `playback`: a copy every browser plays; `original`: as received, which may not play. */
  audioSource: 'playback' | 'original'
  transcript: RecordingTranscript
  /**
   * The recording's file on the case graph: the id of the node the graph shows for it (ADR-0055).
   * Null while the graph shows none: the file is not attached to its subject yet, or was deleted
   * there.
   */
  documentNodeId: string | null
  /**
   * The operator's role as the read granted it (SC-036); a superuser reads as `owner`. Absent (a
   * story) reads as a role that may change the recording.
   */
  memberRole?: CaseRole
  /**
   * Who each voice of the recording is said to be, as the read named them. Null or absent while
   * the recording has no roster: its transcript is not ready, or the graph holds no questioning
   * for it. The page then is what it was.
   */
  voices?: RecordingVoicesData | null
}

/** Whether the operator may say where the recording is held: a viewer only reads it (SC-036). */
export function canChangeRecording(data: Pick<RecordingData, 'memberRole'>): boolean {
  return data.memberRole !== 'viewer'
}

/** Everyone in the recording: its holder first, then the others. */
export function recordingPeople(
  recording: Pick<RecordingData, 'subject' | 'participants'>
): readonly SubjectSummary[] {
  return [recording.subject, ...recording.participants]
}

/**
 * The player's audio: none to play yet, being fetched, ready, or not playable here (the fetch
 * failed, or the browser cannot play what it got).
 */
export type RecordingAudioState = 'none' | 'loading' | 'ready' | 'unavailable'

/** Why the recording page shows no recording: what a refused audited read says of any page. */
export type RecordingProblem = SubjectPageProblem

/** The turn playback is in, and whether it is playing. */
export interface ActiveTurn {
  index: number | null
  playing: boolean
}

// ── Výsluchy: the case's recordings ──────────────────────────────────────────

/**
 * One recording in a list: whose it is, when it was made, how long it is, where its job stands,
 * the encounter it is held in and who else took part.
 */
export type RecordingListItem = Pick<
  RecordingData,
  | 'captureId'
  | 'subject'
  | 'capturedAt'
  | 'state'
  | 'stage'
  | 'failedStage'
  | 'durationMs'
  | 'encounter'
  | 'participants'
>

/** *Výsluchy* as the screen shows it: loading, the rows so far, or why they did not load. */
export type RecordingListView =
  | { status: 'loading' }
  | {
      status: 'ready'
      /** Newest first. */
      recordings: readonly RecordingListItem[]
      /** Every recording of the case. */
      total: number
      hasMore: boolean
      loadingMore: boolean
    }
  | ListReadFailure
